/**
 * Avisos de WappTeam en toda la app (Esteban, 2026-10-09: reemplazo de Cliq con "on screen notifications").
 * Un solo lugar decide si un mensaje nuevo merece aviso: las ventanitas de abajo (wappteamDock) y la pantalla
 * completa de WappTeam (equipoView) dicen qué conversación tienen a la vista para no avisar de lo que ya se está leyendo.
 * Preferencias por computadora (localStorage): sonido y "no molestar".
 */

/** Conversaciones que la persona tiene a la vista ahora mismo (pantalla WappTeam o ventanita abierta). */
const aLaVista = new Set<number>();
export const equipoVista = {
	poner(id: number | null, anterior?: number | null) {
		if (anterior) aLaVista.delete(anterior);
		if (id) aLaVista.add(id);
	},
	quitar(id: number | null) { if (id) aLaVista.delete(id); },
	/** Visible de verdad: la conversación está a la vista Y la pestaña del navegador está al frente. */
	viendo(id: number): boolean { return aLaVista.has(id) && document.visibilityState === "visible"; },
};

const leer = (k: string, def: string): string => { try { return localStorage.getItem(k) ?? def; } catch { return def; } };
const guardar = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sin almacenamiento */ } };

export const prefs = {
	get sonido(): boolean { return leer("wteam.sonido", "1") === "1"; },
	set sonido(v: boolean) { guardar("wteam.sonido", v ? "1" : "0"); },
	get noMolestar(): boolean { return leer("wteam.noMolestar", "0") === "1"; },
	set noMolestar(v: boolean) { guardar("wteam.noMolestar", v ? "1" : "0"); },
	/** Ya se le ofreció activar los avisos del navegador y dijo "Ahora no". */
	get avisoDescartado(): boolean { return leer("wteam.avisoDescartado", "0") === "1"; },
	set avisoDescartado(v: boolean) { guardar("wteam.avisoDescartado", v ? "1" : "0"); },
};

export const permisoAvisos = (): NotificationPermission | "no-soportado" =>
	(typeof Notification === "undefined" ? "no-soportado" : Notification.permission);

export const pedirPermisoAvisos = async (): Promise<NotificationPermission | "no-soportado"> => {
	if (typeof Notification === "undefined") return "no-soportado";
	try { return await Notification.requestPermission(); } catch { return Notification.permission; }
};

/** Aviso del sistema (fuera del navegador). Al tocarlo, vuelve a la pestaña y abre la conversación. */
export const avisoDelSistema = (titulo: string, cuerpo: string, tag: string, alTocar: () => void) => {
	if (permisoAvisos() !== "granted") return;
	try {
		const n = new Notification(titulo, { body: cuerpo, tag, icon: "/favicon.ico" });
		n.onclick = () => { try { window.focus(); } catch { /* */ } alTocar(); n.close(); };
		setTimeout(() => n.close(), 12000);
	} catch { /* el navegador no deja (iPhone fuera de la app instalada) */ }
};

/** "Tin" corto hecho con el navegador (sin archivo de audio). Los navegadores lo callan hasta el primer clic en la página. */
let ctx: AudioContext | null = null;
let ultimoSonido = 0;
export const sonar = () => {
	if (!prefs.sonido || Date.now() - ultimoSonido < 1500) return;
	ultimoSonido = Date.now();
	try {
		const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!AC) return;
		ctx = ctx || new AC();
		const c = ctx as AudioContext;
		if (c.state === "suspended") c.resume().catch(() => null);
		const t = c.currentTime;
		[880, 1320].forEach((f, i) => {
			const o = c.createOscillator();
			const g = c.createGain();
			o.type = "sine";
			o.frequency.value = f;
			g.gain.setValueAtTime(0.0001, t + i * 0.09);
			g.gain.exponentialRampToValueAtTime(0.12, t + i * 0.09 + 0.02);
			g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.09 + 0.18);
			o.connect(g).connect(c.destination);
			o.start(t + i * 0.09);
			o.stop(t + i * 0.09 + 0.2);
		});
	} catch { /* sin audio */ }
};
